import React, { useEffect, useRef, useState } from "react";
import Modal from "./Modal";
import type { SolverPhasesModalProps } from "@/components/modals/SolverPhasesModal.types.tsx";
import SolverPhases, { buildSolverPhasesController } from "@/components/sections/SolverPhases.tsx";
import SolverSettingsModal from "@/components/modals/SolverSettingsModal.tsx";
import { fetchExtendedWgData, updateExtendedWgData, updateSolverConfigs } from "@/api.ts";
import type { ScheduleGenerationSettings } from "@/types/needsContrainsRequest.ts";
import SolverPhaseConfigModal from "@/components/modals/SolverPhaseConfigModal.tsx";
import DateFilterModal from "@/components/modals/DateFilterModal.tsx";
import WeekendGridModal from "@/components/modals/WeekendGridModal.tsx";


/**
 * Main component
 * @param param0
 * @returns
 */
const SolverPhasesModal: React.FC<SolverPhasesModalProps> = (
    {
        open,
        onClose,
        onConfirm,
        title,
        confirmLabel = "Fermer",
        cancelLabel = "Annuler",
        showCancelButton = false,
        confirmDisabled = false,
        onUpdateSolverConfigs,
        onScheduleGenerationSettingsChanged,
        depConfigs = {},
        schedule,
        dateFilter,
        onChangeDateFilter,
        generationStart,
        generationEnd

    }) => {

    const [confirmOpenGlobalSettingModal, setConfirmOpenGlobalSettingModal] = useState(false);
    const [selectedPhaseId, setSelectedPhaseId] = useState<string | undefined>();
    const [confirmOpenPhaseModal, setConfirmOpenPhaseModal] = useState(false);
    const [openDateFilter, setOpenDateFilter] = useState(false);
    const [weekendGridOpen, setWeekendGridOpen] = useState(false);
    const [lookBackwardInDays, setLookBackwardInDays] = useState<number | null>(null);
    const [scheduleGenSettings, setScheduleGenSettings] = useState<ScheduleGenerationSettings>({});
    const [genSettingsStatus, setGenSettingsStatus] = useState<"loading" | "ready" | "error">("loading");
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);
    const ctrl = buildSolverPhasesController(depConfigs, selectedPhaseId);
    const viewData = ctrl.viewData;
    //const [editData , setEditData] = useState({...{},...solverConfigs});


    //@ Reset editData each time modal is opened (Not sure if this is the proper way of doing it)
    useEffect(() => {
        if (open) {
            ctrl.setEditData({ ...{}, ...depConfigs });
        }
    }, [open, depConfigs]);

    useEffect(() => {
        if (!open) {
            return;
        }

        let cancelled = false;
        savingRef.current = false;
        setSaving(false);
        setGenSettingsStatus("loading");
        setSaveError(null);

        fetchExtendedWgData()
            .then((data) => {
                if (cancelled) {
                    return;
                }

                const scheduleGenerationSettings: ScheduleGenerationSettings = data?.scheduleGenerationSettings ?? {};
                setScheduleGenSettings(scheduleGenerationSettings);

                const val = scheduleGenerationSettings.lookBackwardInDays;
                setLookBackwardInDays(typeof val === "number" ? val : null);
                setGenSettingsStatus("ready");
            })
            .catch((err) => {
                if (cancelled) {
                    return;
                }

                console.error("fetchExtendedWgData failed", err);

                setScheduleGenSettings({});
                setLookBackwardInDays(null);
                setGenSettingsStatus("error");
            });
        return () => {
            cancelled = true;
        };
    }, [open]);

    const handleConfirm = async () => {
        if (savingRef.current) {
            return;
        }

        const lookBackwardChanged = genSettingsStatus === "ready"
            && (scheduleGenSettings.lookBackwardInDays ?? null) !== (lookBackwardInDays ?? null);

        savingRef.current = true;
        setSaving(true);
        setSaveError(null);

        try {
            if (lookBackwardChanged) {
                const params = new URLSearchParams(window.location.search);

                await updateExtendedWgData({
                    wgId: params.get("wgId") || "",
                    businessAccountId: params.get("bizId") || "",
                    scheduleGenerationSettings: {
                        ...scheduleGenSettings,
                        lookBackwardInDays,
                    },
                });

                setScheduleGenSettings((prev) => ({ ...prev, lookBackwardInDays }));
                onScheduleGenerationSettingsChanged?.();
            }
        } catch (err) {
            // Closing here would discard what the user typed, with no way to tell the save failed.
            console.error("updateExtendedWgData failed", err);
            setSaveError("Échec de la sauvegarde. Vérifie ta connexion et réessaie.");
            savingRef.current = false;
            setSaving(false);
            return;
        }

        savingRef.current = false;
        setSaving(false);
        onConfirm(ctrl.editData);
        onClose();
    };


    return (
        <Modal
            open={open}
            onClose={onClose}
            title={title || "Sélection de la phase a résoudre"}
            size="md"
            footer={
                <>
                    {saveError && (
                        <p className="mr-auto text-sm text-red-600">{saveError}</p>
                    )}
                    {
                        showCancelButton ? <button
                            onClick={onClose}
                            className="inline-flex items-center rounded-full bg-gray-200 cursor-pointer px-4 py-2 font-medium text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                        >
                            {cancelLabel}
                        </button> : ''
                    }
                    <button
                        onClick={handleConfirm}
                        disabled={confirmDisabled || saving}
                        className={`inline-flex items-center rounded-full bg-green-600 px-4 py-2 font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${confirmDisabled || saving ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-green-700"}`}
                    >
                        {confirmLabel || "Démarrer"}
                    </button>
                </>
            }
        >
            <SolverPhases
                selectGlobalConfig={() => {
                    setConfirmOpenGlobalSettingModal(true)
                }}
                selectDateFilter={() => {
                    setOpenDateFilter(true)
                }}
                addPhase={() => {
                    setSelectedPhaseId("");
                    setConfirmOpenPhaseModal(true)
                }}
                selectPhase={(id) => {
                    setSelectedPhaseId(id);
                    setConfirmOpenPhaseModal(true)
                }}
                controller={ctrl}
                dateFilter={dateFilter}
                lookBackwardInDays={lookBackwardInDays}
                lookBackwardStatus={genSettingsStatus}
                onChangeLookBackwardInDays={setLookBackwardInDays}
                onOpenWeekendGrid={schedule ? () => setWeekendGridOpen(true) : undefined}
            >
            </SolverPhases>
            <DateFilterModal
                open={openDateFilter}
                onClose={() => setOpenDateFilter(false)}
                value={dateFilter ?? null}
                onChange={(value) => onChangeDateFilter?.(value)}
                defaultStart={generationStart}
                defaultEnd={generationEnd}
            />
            <SolverSettingsModal
                open={confirmOpenGlobalSettingModal}
                onClose={() => setConfirmOpenGlobalSettingModal(false)}
                onConfirm={async (editData) => {
                    await updateSolverConfigs(editData);
                    onUpdateSolverConfigs();
                }}
                title="Configurations de résolution"
                confirmDisabled={false}
                solverConfigs={depConfigs}
            />
            {schedule && (
                <WeekendGridModal
                    open={weekendGridOpen}
                    onClose={() => setWeekendGridOpen(false)}
                    roles={schedule.roles ?? []}
                    shifts={schedule.shifts ?? []}
                />
            )}
            <SolverPhaseConfigModal
                schedule={schedule}
                open={confirmOpenPhaseModal}
                onClose={() => setConfirmOpenPhaseModal(false)}
                onConfirm={async (editData, phaseId) => {

                    // var newMapOfPhase:typeof ctrl.editData.mapOfPhases = {}
                    var updatedData = {
                        ...{},
                        ...ctrl.editData,
                        ...{
                            mapOfPhases: {
                                ...{},
                                ...(ctrl.editData.mapOfPhases || {}),
                                ...{
                                    [phaseId || "new"]: editData
                                }
                            }
                        }
                    };

                    ctrl.setEditData(updatedData)
                    await updateSolverConfigs(updatedData);
                    onUpdateSolverConfigs();
                }}
                onRemove={async (phaseId) => {
                    if (phaseId) {
                        var newMapOfPhases: typeof ctrl.editData.mapOfPhases = { ...{}, ...(ctrl.editData.mapOfPhases || {}) };
                        delete newMapOfPhases[phaseId || ""];
                        var updatedData = {
                            ...{},
                            ...ctrl.editData,
                            ...{
                                mapOfPhases: newMapOfPhases
                            }
                        };

                        ctrl.setEditData(updatedData)
                        await updateSolverConfigs(updatedData);
                        onUpdateSolverConfigs();
                    }
                }}
                title="Configuration de la phase"
                confirmDisabled={false}
                phase={viewData.selectedPhase}
                phaseId={viewData.selectedPhaseId}

            />
        </Modal>
    );
};

export default SolverPhasesModal;
